import Room from "@/domain/restaurant/Room";
import ClosedPeriod from "@/domain/restaurant/ClosedPeriod";
import WorkingHours from "@/domain/restaurant/WorkingHours";
import UtilService from "@/services/UtilService";
import Booking from "@/domain/booking/Booking";
import moment from "moment-timezone";
import TimeService from "@/services/TimeService";
import i18n from "@/i18n";

export default class Restaurant {
  restaurantId!: number;
  restaurantName!: string;
  rooms: Room[] = [];
  holidays: ClosedPeriod[] = [];
  minMinutesBetweenSeatings!: number;
  minMinutesBeforeBookingLocked!: number;
  maxBookingsAtTime!: number;
  maxPeopleAtTime!: number;
  bookingInterval!: number;
  workingHours: WorkingHours[] = [];
  closedDaysOfWeek: string[] = [];
  timeZone!: string;
  specialOpenDays!: string[];
  sendSMS!: boolean;
  phoneNumber!: string;
  sendEmail!: boolean;
  email!: string;
  onlineBookingMaxSize!: number;
  maps!: string;
  notes!: string;
  onlineBookingLink!: string;

  constructor(jsonObj?: Restaurant) {
    if (jsonObj) {
      Restaurant.updateRestaurant(this, jsonObj);
    }
  }

  static updateRestaurant(restToUpdate: Restaurant, updatedCopy: Restaurant) {
    restToUpdate.restaurantId = updatedCopy.restaurantId;
    restToUpdate.restaurantName = updatedCopy.restaurantName;
    restToUpdate.rooms = updatedCopy.rooms;
    restToUpdate.holidays = updatedCopy.holidays;
    restToUpdate.minMinutesBetweenSeatings =
      updatedCopy.minMinutesBetweenSeatings;
    restToUpdate.minMinutesBeforeBookingLocked =
      updatedCopy.minMinutesBeforeBookingLocked;
    restToUpdate.maxBookingsAtTime = updatedCopy.maxBookingsAtTime;
    restToUpdate.maxPeopleAtTime = updatedCopy.maxPeopleAtTime;
    restToUpdate.bookingInterval = updatedCopy.bookingInterval;
    restToUpdate.workingHours = updatedCopy.workingHours;
    restToUpdate.closedDaysOfWeek = updatedCopy.closedDaysOfWeek;
    restToUpdate.timeZone = updatedCopy.timeZone;
    restToUpdate.specialOpenDays = updatedCopy.specialOpenDays;
    restToUpdate.sendSMS = updatedCopy.sendSMS;
    restToUpdate.phoneNumber = updatedCopy.phoneNumber;
    restToUpdate.sendEmail = updatedCopy.sendEmail;
    restToUpdate.email = updatedCopy.email;
    restToUpdate.onlineBookingMaxSize = updatedCopy.onlineBookingMaxSize;
    restToUpdate.maps = updatedCopy.maps;
    restToUpdate.notes = updatedCopy.notes;
    restToUpdate.onlineBookingLink = updatedCopy.onlineBookingLink;

  }

  static copy(restaurantToCopy: Restaurant): Restaurant {
    return new Restaurant(JSON.parse(JSON.stringify(restaurantToCopy)));
  }

  static haveDifferences(rest1: Restaurant, rest2: Restaurant): boolean {
    return !UtilService.equals(rest1, rest2);
  }

  static isOpenOnDate(restaurant: Restaurant, date: Date): boolean {
    for (let i = 0; i < restaurant.specialOpenDays.length; i++) {
      const restaurantDate = new Date(restaurant.specialOpenDays[i]);
      TimeService.setDateToStartOfDay(restaurantDate);
      if (restaurantDate.getTime() === date.getTime()) return true;
    }

    for (let i = 0; i < restaurant.holidays.length; i++) {
      const holiday = restaurant.holidays[i];
      if (holiday != null && ClosedPeriod.isActiveOnDate(holiday, date))
        return false;
    }
    for (let i = 0; i < restaurant.closedDaysOfWeek.length; i++) {
      if (
        TimeService.weekDays[date.getDay()].value ===
        restaurant.closedDaysOfWeek[i]
      )
        return false;
    }
    return true;
  }

  static getReasonIsClosedOnDate(restaurant: Restaurant, date: Date): string {
    for (let i = 0; i < restaurant.specialOpenDays.length; i++) {
      const restaurantDate = new Date(restaurant.specialOpenDays[i]);
      TimeService.setDateToStartOfDay(restaurantDate);
      if (restaurantDate.getTime() === date.getTime()) return "";
    }
    for (let i = 0; i < restaurant.holidays.length; i++) {
      const holiday = restaurant.holidays[i];
      if (holiday != null && ClosedPeriod.isActiveOnDate(holiday, date))
        return ClosedPeriod.restaurantClosedForHolidayDisplayString(
          holiday,
          date
        );
    }
    for (let i = 0; i < restaurant.closedDaysOfWeek.length; i++) {
      if (
        TimeService.weekDays[date.getDay()].value ===
        restaurant.closedDaysOfWeek[i]
      )
        return (
          i18n.tc("restaurant.closed.dayOfWeek") +
          TimeService.weekDays[date.getDay()].text
        );
    }
    return "";
  }

  static getLightweightCopyToSend(
    original: Restaurant,
    changed: Restaurant
  ): Restaurant {
    const toReturn = new Restaurant();
    toReturn.restaurantId = changed.restaurantId;
    /* Dont Change Id */
    toReturn.restaurantName = this.clearIfUnchanged(
      original.restaurantName,
      changed.restaurantName
    );
    toReturn.minMinutesBetweenSeatings = this.clearIfUnchanged(
      original.minMinutesBetweenSeatings,
      changed.minMinutesBetweenSeatings
    );
    toReturn.minMinutesBeforeBookingLocked = this.clearIfUnchanged(
      original.minMinutesBeforeBookingLocked,
      changed.minMinutesBeforeBookingLocked
    );
    toReturn.maxBookingsAtTime = this.clearIfUnchanged(
      original.maxBookingsAtTime,
      changed.maxBookingsAtTime
    );
    toReturn.maxPeopleAtTime = this.clearIfUnchanged(
      original.maxPeopleAtTime,
      changed.maxPeopleAtTime
    );
    toReturn.bookingInterval = this.clearIfUnchanged(
      original.bookingInterval,
      changed.bookingInterval
    );
    toReturn.timeZone = this.clearIfUnchanged(
      original.timeZone,
      changed.timeZone
    );
    toReturn.closedDaysOfWeek = JSON.parse(
      this.clearIfUnchanged(
        JSON.stringify(original.closedDaysOfWeek),
        JSON.stringify(changed.closedDaysOfWeek)
      )
    );
    toReturn.sendSMS = this.clearIfUnchanged(original.sendSMS, changed.sendSMS);

    toReturn.phoneNumber = this.clearIfUnchanged(
      original.phoneNumber,
      changed.phoneNumber
    );
    toReturn.sendEmail = this.clearIfUnchanged(
      original.sendEmail,
      changed.sendEmail
    );

    toReturn.email = changed.email;

    toReturn.notes = changed.notes;

    toReturn.maps = changed.maps;

    /* These lists are never manipulated through the object, but directly through their elements id */
    toReturn.workingHours = [];
    toReturn.rooms = [];
    toReturn.holidays = [];

    return toReturn;
  }

  static clearIfUnchanged(originalVal: any, newVal: any) {
    if (originalVal === newVal) {
      return null;
    }
    return newVal;
  }

  static BookingTooLateToMove(restaurant: Restaurant, booking: Booking) {
    const bookingDateTime = moment(booking.bookingDateTime);
    const timeAtRestaurant = Restaurant.getDateTimeAtRestaurant(restaurant);
    return (
      bookingDateTime.isSame(timeAtRestaurant, "day") &&
      bookingDateTime.isBefore(
        timeAtRestaurant.add(
          restaurant.minMinutesBeforeBookingLocked,
          "minutes"
        )
      )
    );
  }

  static getDateTimeAtRestaurant(restaurant: Restaurant) {
    return moment().tz(restaurant.timeZone);
  }

  static allowMarkAsSeated(date: Date, restaurant: Restaurant) {
    const dateTime = Restaurant.getDateTimeAtRestaurant(restaurant);
    return (
      date.getTime() <
      dateTime
        .add(8, "hours")
        .toDate()
        .getTime()
    );
  }

  static RestaurantsByNameAscendingComparator = (
    restaurant1: Restaurant,
    restaurant2: Restaurant
  ) => {
    const name1 = restaurant1.restaurantName.toString().toUpperCase();
    const name2 = restaurant2.restaurantName.toString().toUpperCase();
    return UtilService.stringComparatorAscending(name1, name2);
  };

  static getAllPossibleTimesInRestaurant(restaurant: Restaurant) {
    const times: string[] = [];
    restaurant.workingHours.forEach(wh =>
      times.push(
        ...TimeService.getTimesBetweenWithInterval(
          wh.startTime,
          wh.endTime,
          restaurant.bookingInterval
        )
      )
    );
    times.sort();
    return times;
  }
}
